import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import TabNavigation from "components/ui/TabNavigation";
import ProgressIndicator from "components/ui/ProgressIndicator";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

import QuestionCard from "./components/QuestionCard";
import NavigationControls from "./components/NavigationControls";
import QuestionOverviewPanel from "./components/QuestionOverviewPanel";
import SubmitConfirmModal from "./components/SubmitConfirmModal";
import MobileOverviewDrawer from "./components/MobileOverviewDrawer";

const QuizParticipationInterface = () => {
  const navigate = useNavigate();

  const [participantName, setParticipantName] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const [quiz, setQuiz] = useState({
    title: "QCM Sensibilisation Cybersécurité",
    description: "",
    questions: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);

  const totalQuestions = quiz?.questions?.length || 0;
  const currentQuestion = quiz?.questions?.[currentIndex];
  const completedQuestions = Object.keys(answers)?.map((k) => parseInt(k) + 1);
  const answeredCount = Object.keys(answers)?.length;

  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:5002/v1/api";
      // Charge toutes les questions (150) en ordre aléatoire côté backend
      const resp = await axios.get(`${baseUrl}/qcm`);
      const rawQuestions = resp?.data?.data?.questions || [];
      // Nettoyage des ** éventuels dans les textes/options côté front
      const questions = rawQuestions.map((q) => ({
        ...q,
        text: (q.text || "").replace(/\*\*/g, ""),
        options: (q.options || []).map((opt) => (opt || "").replace(/\*\*/g, "")),
      }));
      setQuiz((prev) => ({ ...prev, questions }));
      setCurrentIndex(0);
      setAnswers({});
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les questions du QCM.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = async () => {
    if (!participantName || !participantEmail) {
      setError("Merci de saisir votre prénom et votre email pour commencer.");
      return;
    }
    await loadQuestions();
  };

  const handleAnswerSelect = useCallback(
    (optionIndex) => {
      setAnswers((prev) => ({ ...prev, [currentIndex]: optionIndex }));
    },
    [currentIndex]
  );

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) setCurrentIndex((i) => i + 1);
  };

  const handleNavigate = (idx) => {
    setCurrentIndex(idx);
  };

  const handleSubmitRequest = () => {
    if (!participantName || !participantEmail) {
      setError("Merci de saisir votre prénom et votre email.");
      return;
    }
    if (!totalQuestions) {
      setError('Le QCM n\'est pas chargé. Cliquez sur "Commencer le QCM".');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:5002/v1/api";
      const payload = {
        name: participantName,
        email: participantEmail,
        answers: quiz?.questions?.map((q, i) => ({
          questionId: q.id,
          selectedOptionIndex: answers?.[i] !== undefined ? answers?.[i] : null,
        })),
      };
      const resp = await axios.post(`${baseUrl}/qcm/submit`, payload);
      const data = resp?.data?.data;
      // On ignore le détail ici, il est stocké côté backend ; on affiche juste une page de remerciement
      if (data) {
        navigate("/merci");
      } else {
        setError("Une erreur est survenue lors de l'enregistrement de vos réponses.");
      }
    } catch (e) {
      console.error(e);
      setError("Erreur lors de la soumission du QCM. Merci de réessayer.");
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "var(--color-background)" }}
    >
      <TabNavigation />
      {/* Top spacing for fixed nav */}
      <div className="pt-[60px] md:pt-[60px]" />
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8 pb-20 md:pb-8">
        {/* Quiz Header */}
        <div className="mb-4 md:mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="BookOpen" size={16} color="var(--color-primary)" strokeWidth={2} />
            <span
              className="text-xs font-caption uppercase tracking-widest"
              style={{ color: "var(--color-muted-foreground)" }}
            >
              QCM Sensibilisation Cybersécurité
            </span>
          </div>
          <h1
            className="text-xl md:text-2xl lg:text-3xl font-bold"
            style={{ color: "var(--color-text-primary)" }}
          >
            QCM Sensibilisation Cyber<strong>sécurité</strong>
          </h1>
          <p
            className="text-sm md:text-base mt-1"
            style={{ color: "var(--color-muted-foreground)" }}
          >
            {quiz?.description}
          </p>
        </div>

        {/* Identité avant de commencer */}
        <div
          className="rounded-xl p-4 md:p-5 mb-4 md:mb-6 grid grid-cols-1 md:grid-cols-3 gap-4"
          style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}
        >
          <div>
            <label className="block text-xs font-caption mb-1" style={{ color: "var(--color-muted-foreground)" }}>
              Prénom / Nom
            </label>
            <input
              type="text"
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm border"
              style={{
                backgroundColor: "var(--color-background)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="Votre nom"
            />
          </div>
          <div>
            <label className="block text-xs font-caption mb-1" style={{ color: "var(--color-muted-foreground)" }}>
              Email
            </label>
            <input
              type="email"
              value={participantEmail}
              onChange={(e) => setParticipantEmail(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm border"
              style={{
                backgroundColor: "var(--color-background)",
                borderColor: "var(--color-border)",
                color: "var(--color-text-primary)",
              }}
              placeholder="vous@exemple.com"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="default"
              fullWidth
              onClick={handleStartQuiz}
              loading={loading}
              iconName="PlayCircle"
              iconPosition="left"
            >
              Commencer le QCM
            </Button>
          </div>
          {error && (
            <div className="md:col-span-3 text-xs font-caption" style={{ color: "var(--color-error)" }}>
              {error}
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        {totalQuestions > 0 && (
          <div
            className="rounded-xl p-4 md:p-5 mb-4 md:mb-6"
            style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}
          >
            <ProgressIndicator
              currentQuestion={currentIndex + 1}
              totalQuestions={totalQuestions}
              completedQuestions={completedQuestions}
              onQuestionSelect={(qNum) => handleNavigate(qNum - 1)}
              showSteps={true}
            />
          </div>
        )}

        {/* Main Content Grid */}
        {totalQuestions > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Question Area */}
          <div className="lg:col-span-2 space-y-4">
            <QuestionCard
              question={currentQuestion}
              questionIndex={currentIndex}
              totalQuestions={totalQuestions}
              selectedAnswer={answers?.[currentIndex] !== undefined ? answers?.[currentIndex] : null}
              onAnswerSelect={handleAnswerSelect}
            />

            <NavigationControls
              currentIndex={currentIndex}
              totalQuestions={totalQuestions}
              hasAnswer={answers?.[currentIndex] !== undefined}
              onPrev={handlePrev}
              onNext={handleNext}
              onSubmit={handleSubmitRequest}
            />
          </div>

          {/* Overview Panel - Desktop */}
          <div className="hidden lg:block">
            <QuestionOverviewPanel
              totalQuestions={totalQuestions}
              answers={answers}
              currentIndex={currentIndex}
              onNavigate={handleNavigate}
            />

            {/* Submit button in sidebar */}
            <div className="mt-4">
              <Button
                variant="success"
                fullWidth
                onClick={handleSubmitRequest}
                iconName="Send"
                iconPosition="right"
              >
                Terminer le QCM
              </Button>
              <p
                className="text-xs text-center mt-2 font-caption"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {answeredCount}/{totalQuestions} questions répondues
              </p>
            </div>
          </div>
        </div>
        )}
      </main>
      {/* Mobile: Fixed bottom bar with overview trigger */}
      <div
        className="fixed bottom-0 left-0 right-0 flex md:hidden items-center justify-between px-4 py-3 z-30"
        style={{
          backgroundColor: "var(--color-card)",
          borderTop: "1px solid var(--color-border)",
          boxShadow: "0 -2px 8px rgba(15,23,42,0.08)",
        }}
      >
        <button
          onClick={() => setShowMobileDrawer(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ backgroundColor: "var(--color-muted)" }}
        >
          <Icon name="LayoutGrid" size={16} color="var(--color-primary)" />
          <span className="text-xs font-caption font-medium" style={{ color: "var(--color-primary)" }}>
            {answeredCount}/{totalQuestions}
          </span>
        </button>

        <Button
          variant="success"
          size="sm"
          onClick={handleSubmitRequest}
          iconName="Send"
          iconPosition="right"
        >
          Terminer
        </Button>
      </div>
      {/* Mobile Drawer */}
      <MobileOverviewDrawer
        isOpen={showMobileDrawer}
        onClose={() => setShowMobileDrawer(false)}
        totalQuestions={totalQuestions}
        answers={answers}
        currentIndex={currentIndex}
        onNavigate={handleNavigate}
      />
      {/* Confirm Modal */}
      {showConfirmModal && (
        <SubmitConfirmModal
          totalQuestions={totalQuestions}
          answeredCount={answeredCount}
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};

export default QuizParticipationInterface;